/**
 * Empty-thread starter sets for `MessageList` — the STRATEGY objects behind
 * the "pick a starting point" bubbles. One `MessageList` implementation
 * renders whichever set its surface injects (`ChatWindow.starterSet` →
 * `MessageList.starterSet`); surfaces vary by DATA, never by forking the
 * component (frontend-doctrine §4.14 one-implementation rule).
 *
 * Clicking a starter prefills the composer (never auto-sends) so the user
 * can tweak before sending. Pure frontend: no new backend path.
 *
 * DEFAULT_CHAT_STARTERS carries NO [bracketed] fill-in slots (onboarding-v2
 * §3.6). A prompt that ships a placeholder the user must find and replace is
 * an instruction, not a starting point, and it reads as unfinished. Every
 * default prompt must be sendable as-is; a regression test pins that.
 *
 * Consumers: the chat route uses `DEFAULT_CHAT_STARTERS` (general-purpose
 * asks); the workflows gestation route injects `WORKFLOW_CHAT_STARTERS`
 * (`components/workflows/workflowChatStarters.ts`) — spec-shaped prompts
 * only, since that thread exists to author a praxis spec.
 */

import { Newspaper, Search } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface ChatStarter {
  readonly id: string;
  readonly icon: LucideIcon;
  readonly tone: string;
  readonly label: string;
  readonly hint: string;
  readonly prompt: string;
}

export interface ChatStarterSet {
  /** The line above the bubbles ("Pick a starting point — or just type below."). */
  readonly intro: string;
  readonly starters: readonly ChatStarter[];
}

export const DEFAULT_CHAT_STARTERS: ChatStarterSet = {
  intro: "Pick a starting point — or just type below.",
  starters: [
    {
      id: "briefing",
      icon: Newspaper,
      tone: "bg-home-sky/25 text-home-water",
      label: "Daily briefing",
      hint: "What changed today",
      prompt:
        "Give me a short daily briefing of what changed in my industry today, with sources I can check.",
    },
    {
      id: "competitors",
      icon: Search,
      tone: "bg-home-candy/15 text-home-candy",
      label: "Watch competitors",
      hint: "Track their moves",
      prompt:
        "Watch my competitors and send me what changed this week — launches, pricing, announcements — with links.",
    },
  ],
};
